import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { DataGrid, GridColDef, GridPaginationModel, GridRowSelectionModel, GridSortModel } from '@mui/x-data-grid';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import {
  Box,
  Button,
  Chip,
  InputAdornment,
  MenuItem,
  Stack,
  TextField,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import FlashOnIcon from '@mui/icons-material/FlashOnOutlined';
import UploadFileIcon from '@mui/icons-material/UploadFileOutlined';
import DownloadIcon from '@mui/icons-material/DownloadOutlined';
import GroupIcon from '@mui/icons-material/GroupOutlined';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import SwapHorizIcon from '@mui/icons-material/SwapHorizOutlined';
import PlaylistAddCheckIcon from '@mui/icons-material/PlaylistAddCheckOutlined';
import ContactsIcon from '@mui/icons-material/ContactsOutlined';
import { exportLeadsCsv, listLeads, bulkAssignLeads, bulkTransferLeads, bulkChangeLeadStatus } from '../../api/leads';
import { bulkSendWhatsApp } from '../../api/whatsapp';
import { listLookup } from '../../api/masterData';
import { AssignmentType, LeadListItem } from '../../types/lead';
import { downloadBlob } from '../../utils/downloadBlob';
import { leadDetailState } from '../../utils/leadNavigation';
import { useBranchFilter } from '../../context/branchContext';
import { useAuth } from '../../context/authContext';
import { useCounselorOptions } from '../../hooks/useCounselorOptions';
import { TableToolbar } from '../../components/table/TableToolbar';
import { SavedFiltersBar } from '../../components/table/SavedFiltersBar';
import { PageHeader } from '../../components/PageHeader';
import { LeadRegistrationWizard } from './wizard/LeadRegistrationWizard';
import { BulkAssignDialog } from './BulkAssignDialog';
import { BulkWhatsAppDialog } from './BulkWhatsAppDialog';
import { BulkTransferDialog } from './BulkTransferDialog';
import { BulkStatusDialog } from './BulkStatusDialog';
import { ImportLeadsDialog } from './ImportLeadsDialog';
import { QuickLeadDialog } from './QuickLeadDialog';
import { GLOBAL_BRANCH_SCOPE_ROLES } from '../../utils/roleScope';
import { ApprovalStatus } from '../../types/lead';

const COUNSELOR_ROLE = 'Counselor';

const APPROVAL_LABELS: Record<ApprovalStatus, string> = {
  PENDING_APPROVAL: 'Pending Approval',
  APPROVED: 'Approved',
  REJECTED: 'Rejected',
};

const PAGE_SIZE_OPTIONS = [10, 20, 50, 100];
const DEFAULT_PAGE_SIZE = 20;
const APPROVAL_STATUSES: ApprovalStatus[] = ['PENDING_APPROVAL', 'APPROVED', 'REJECTED'];
const ASSIGNMENT_TYPES: AssignmentType[] = ['UNASSIGNED', 'COUNSELOR_CREATED', 'CENTRAL_ASSIGNED', 'TRANSFERRED'];

export const LeadsListPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const { selectedBranchId } = useBranchFilter();
  const { user, hasPermission } = useAuth();
  // The backend already scopes the underlying query correctly for every
  // role (leadOwnerScopeWhere/branchScopeWhere in leads.service.ts) — this
  // is purely a label, matching Phase 6/7's "clearly labelled" ask, not a
  // second ownership filter. Super Admin/Director/Branch Manager etc. all
  // see "All Leads"; only a Counselor sees "My Leads".
  const isCounselor = user?.role.name === COUNSELOR_ROLE;
  const { counselors } = useCounselorOptions(!isCounselor);

  // List state lives in the URL (2026-11-05): page, page size, search,
  // every filter and the sort order are query parameters, so opening a lead
  // and coming back (the Back button or the browser's), a refresh or a
  // shared link all show exactly the same list — and the first request
  // already asks for the right page. Changes replace the current history
  // entry rather than adding one per keystroke. Dashboard click-throughs
  // (?assignedToId=…&pending=true, ?leadStatusId=…) use the same names.
  const [params, setParams] = useSearchParams();
  const idParam = (key: string): number | '' => {
    const v = params.get(key);
    return v && /^\d+$/.test(v) ? Number(v) : '';
  };
  const oneOf = <T extends string>(key: string, allowed: readonly T[]): T | '' => {
    const v = params.get(key) as T | null;
    return v && allowed.includes(v) ? v : '';
  };
  const pageSize = PAGE_SIZE_OPTIONS.includes(Number(params.get('pageSize'))) ? Number(params.get('pageSize')) : DEFAULT_PAGE_SIZE;
  const paginationModel: GridPaginationModel = { page: Math.max(1, Number(params.get('page')) || 1) - 1, pageSize };
  const search = params.get('search') ?? '';
  const statusId = idParam('leadStatusId');
  const sourceId = idParam('leadSourceId');
  // Super Admin / non-Counselor "All Leads" extra filters (Phase 7) —
  // hidden for Counselors, who never need to filter by someone else's
  // ownership on their own already-owner-scoped view.
  const assignedToId = idParam('assignedToId');
  const assignmentStatus = oneOf('assignment', ['assigned', 'unassigned'] as const);
  const assignmentType = oneOf<AssignmentType>('assignmentType', ASSIGNMENT_TYPES);
  // Pending Leads by Counselor / "My Pending Leads" — still requires
  // Counselor action: not terminal, approved, not yet converted to a
  // Student (see PENDING_LEAD_WHERE, backend/src/modules/leads/leads.service.ts).
  // Visible to every role (not just non-Counselor), since a Counselor's
  // own "My Pending Leads" KPI links here too.
  const pendingOnly = oneOf('pending', ['true', 'false'] as const);
  const approvalStatus = oneOf<ApprovalStatus>('approvalStatus', APPROVAL_STATUSES);
  const sortParam = params.get('sort');
  const sortModel: GridSortModel = sortParam && /^\w+:(asc|desc)$/.test(sortParam)
    ? [{ field: sortParam.split(':')[0], sort: sortParam.split(':')[1] as 'asc' | 'desc' }]
    : [];

  /** Set (or, when empty/default, remove) list parameters; a changed filter
   * or search goes back to page 1 (as before), paging itself doesn't. */
  const updateParams = (changes: Record<string, string | number | '' | null>, { resetPage = false } = {}) =>
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        for (const [key, value] of Object.entries(changes)) {
          if (value === '' || value === null) next.delete(key);
          else next.set(key, String(value));
        }
        if (resetPage) next.delete('page');
        return next;
      },
      { replace: true },
    );
  const setPaginationModel = (model: GridPaginationModel) =>
    updateParams({ page: model.page > 0 ? model.page + 1 : null, pageSize: model.pageSize !== DEFAULT_PAGE_SIZE ? model.pageSize : null });
  const setSortModel = (model: GridSortModel) => updateParams({ sort: model[0]?.sort ? `${model[0].field}:${model[0].sort}` : null });

  const [selection, setSelection] = useState<GridRowSelectionModel>([]);

  const [wizardOpen, setWizardOpen] = useState(false);
  const [quickLeadOpen, setQuickLeadOpen] = useState(false);
  const [resumeLeadId, setResumeLeadId] = useState<number | null>(null);
  const [bulkAssignOpen, setBulkAssignOpen] = useState(false);
  const [bulkAssignError, setBulkAssignError] = useState<string | null>(null);
  const [bulkWhatsAppOpen, setBulkWhatsAppOpen] = useState(false);
  const [bulkWhatsAppError, setBulkWhatsAppError] = useState<string | null>(null);
  const [bulkWhatsAppResult, setBulkWhatsAppResult] = useState<{
    total: number;
    sent: number;
    failed: number;
  } | null>(null);
  const [bulkTransferOpen, setBulkTransferOpen] = useState(false);
  const [bulkTransferError, setBulkTransferError] = useState<string | null>(null);
  const [bulkStatusOpen, setBulkStatusOpen] = useState(false);
  const [bulkStatusError, setBulkStatusError] = useState<string | null>(null);
  const [importOpen, setImportOpen] = useState(false);

  useEffect(() => {
    const state = location.state as { quickCreate?: boolean; openQuickLead?: boolean } | null;
    // "+ New" menu bug fix: this must depend on location.state (not `[]`,
    // "run once on mount"). The "+ New" menu navigates to this same
    // /leads route via `navigate('/leads', {state})` — when the user is
    // already ON /leads, React Router does NOT unmount/remount this
    // component (same route match), it only updates location.state on
    // the *existing* instance. A mount-only effect would have already
    // fired (with whatever state existed at first mount, typically none)
    // and would never see this new state — which is exactly why "+ New"
    // items previously appeared to do nothing when clicked from this
    // page itself. Depending on location.state instead re-runs this
    // effect on every navigation, remount or not.
    // (The query string — the list's page and filters — is kept.)
    if (state?.openQuickLead) {
      setQuickLeadOpen(true);
      navigate(`${location.pathname}${location.search}`, { replace: true, state: null });
    } else if (state?.quickCreate) {
      setResumeLeadId(null);
      setWizardOpen(true);
      navigate(`${location.pathname}${location.search}`, { replace: true, state: null });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state]);

  const filters = {
    search: search || undefined,
    leadStatusId: statusId || undefined,
    leadSourceId: sourceId || undefined,
    branchId: selectedBranchId,
    assignedToId: assignedToId || undefined,
    assignmentType: assignmentType || undefined,
    approvalStatus: approvalStatus || undefined,
    unassigned: assignmentStatus ? assignmentStatus === 'unassigned' : undefined,
    pending: pendingOnly ? pendingOnly === 'true' : undefined,
  };

  const leadsQuery = useQuery({
    queryKey: ['leads', paginationModel.page, paginationModel.pageSize, filters],
    queryFn: () =>
      listLeads({
        page: paginationModel.page + 1,
        pageSize: paginationModel.pageSize,
        ...filters,
      }),
    placeholderData: (prev) => prev,
  });

  const statusesQuery = useQuery({
    queryKey: ['lookup', '/master-data/lead-statuses'],
    queryFn: () => listLookup('/master-data/lead-statuses'),
  });
  const sourcesQuery = useQuery({
    queryKey: ['lookup', '/master-data/lead-sources'],
    queryFn: () => listLookup('/master-data/lead-sources'),
  });

  const invalidateLeads = () => queryClient.invalidateQueries({ queryKey: ['leads'] });

  const bulkAssignMutation = useMutation({
    mutationFn: ({ userId, reason }: { userId: number; reason?: string }) =>
      bulkAssignLeads(selection as number[], userId, reason),
    onSuccess: () => {
      invalidateLeads();
      setBulkAssignOpen(false);
      setBulkAssignError(null);
      setSelection([]);
    },
    onError: (err) => setBulkAssignError(extractErrorMessage(err)),
  });

  const bulkWhatsAppMutation = useMutation({
    mutationFn: (input: { templateId?: number; body?: string }) =>
      bulkSendWhatsApp({ leadIds: selection as number[], ...input }),
    onSuccess: (result) => {
      setBulkWhatsAppResult(result);
      setBulkWhatsAppError(null);
    },
    onError: (err) => setBulkWhatsAppError(extractErrorMessage(err)),
  });

  const bulkTransferMutation = useMutation({
    mutationFn: ({ toBranchId, reason }: { toBranchId: number; reason?: string }) =>
      bulkTransferLeads(selection as number[], toBranchId, reason),
    onSuccess: () => {
      invalidateLeads();
      setBulkTransferOpen(false);
      setBulkTransferError(null);
      setSelection([]);
    },
    onError: (err) => setBulkTransferError(extractErrorMessage(err)),
  });

  const bulkStatusMutation = useMutation({
    mutationFn: (leadStatusId: number) => bulkChangeLeadStatus(selection as number[], leadStatusId),
    onSuccess: () => {
      invalidateLeads();
      setBulkStatusOpen(false);
      setBulkStatusError(null);
      setSelection([]);
    },
    onError: (err) => setBulkStatusError(extractErrorMessage(err)),
  });

  const handleExport = async () => {
    const blob = await exportLeadsCsv(filters);
    downloadBlob(blob, `leads-export-${Date.now()}.csv`);
  };

  const columns: GridColDef<LeadListItem>[] = useMemo(
    () => [
      { field: 'leadNumber', headerName: 'Lead #', width: 110 },
      {
        field: 'name',
        headerName: 'Name',
        flex: 1,
        valueGetter: (_v, row) => `${row.firstName} ${row.lastName}`,
      },
      { field: 'phone', headerName: 'Phone', width: 140 },
      {
        field: 'leadSource',
        headerName: 'Source',
        width: 130,
        valueGetter: (_v, row) => row.leadSource.name,
      },
      {
        field: 'adIdName',
        headerName: 'Ad ID Name',
        width: 150,
        valueGetter: (_v, row) => row.adIdName ?? '',
      },
      {
        field: 'leadStatus',
        headerName: 'Status',
        width: 150,
        renderCell: (params) => (
          <Chip
            size="small"
            label={params.row.leadStatus.name}
            sx={{ bgcolor: params.row.leadStatus.colorHex, color: '#fff' }}
          />
        ),
      },
      {
        field: 'assignedTo',
        headerName: 'Assigned To',
        width: 160,
        valueGetter: (_v, row) => (row.assignedTo ? `${row.assignedTo.firstName} ${row.assignedTo.lastName}` : '—'),
      },
      {
        field: 'branch',
        headerName: 'Branch',
        width: 130,
        valueGetter: (_v, row) => row.branch?.name ?? '—',
      },
      {
        field: 'approvalStatus',
        headerName: 'Approval',
        width: 150,
        renderCell: (params) => (
          <Chip
            size="small"
            label={APPROVAL_LABELS[params.row.approvalStatus]}
            color={
              params.row.approvalStatus === 'APPROVED'
                ? 'success'
                : params.row.approvalStatus === 'REJECTED'
                  ? 'error'
                  : 'warning'
            }
          />
        ),
      },
    ],
    [],
  );

  const isGlobalScope = !!user && GLOBAL_BRANCH_SCOPE_ROLES.includes(user.role.name);
  const pageTitle = isCounselor ? 'My Leads' : isGlobalScope ? 'All Leads' : 'Leads';
  const pageSubtitle = isCounselor
    ? 'Leads currently assigned to you.'
    : isGlobalScope
      ? 'Every lead across every branch and counselor.'
      : 'Track and manage inbound leads across all sources.';

  return (
    <Box sx={{ p: 3 }}>
      <PageHeader
        icon={ContactsIcon}
        color="#5B2DA3"
        title={pageTitle}
        subtitle={pageSubtitle}
        actions={
          <>
            {selection.length > 0 && hasPermission('leads', 'assign') && (
            <Button
              startIcon={<GroupIcon />}
              onClick={() => {
                setBulkAssignError(null);
                setBulkAssignOpen(true);
              }}
            >
              Assign ({selection.length})
            </Button>
          )}
          {selection.length > 0 && (
            <Button
              startIcon={<WhatsAppIcon />}
              onClick={() => {
                setBulkWhatsAppError(null);
                setBulkWhatsAppResult(null);
                setBulkWhatsAppOpen(true);
              }}
            >
              WhatsApp ({selection.length})
            </Button>
          )}
          {selection.length > 0 && hasPermission('leads', 'transfer') && (
            <Button
              startIcon={<SwapHorizIcon />}
              onClick={() => {
                setBulkTransferError(null);
                setBulkTransferOpen(true);
              }}
            >
              Transfer ({selection.length})
            </Button>
          )}
          {selection.length > 0 && hasPermission('leads', 'update') && (
            <Button
              startIcon={<PlaylistAddCheckIcon />}
              onClick={() => {
                setBulkStatusError(null);
                setBulkStatusOpen(true);
              }}
            >
              Status ({selection.length})
            </Button>
          )}
          <Button startIcon={<DownloadIcon />} onClick={handleExport}>
            Export
          </Button>
          {hasPermission('leads', 'import') && (
            <Button startIcon={<UploadFileIcon />} onClick={() => setImportOpen(true)}>
              Import
            </Button>
          )}
          {hasPermission('leads', 'create') && (
            <Button startIcon={<FlashOnIcon />} onClick={() => setQuickLeadOpen(true)}>
              Quick Lead
            </Button>
          )}
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => {
              setResumeLeadId(null);
              setWizardOpen(true);
            }}
          >
            New lead
          </Button>
          </>
        }
      />

      <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
        <TextField
          placeholder="Search name, phone, email, lead #…"
          size="small"
          sx={{ width: 300 }}
          value={search}
          onChange={(e) => updateParams({ search: e.target.value }, { resetPage: true })}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
        <TextField
          select
          size="small"
          label="Status"
          sx={{ width: 180 }}
          value={statusId}
          onChange={(e) => updateParams({ leadStatusId: e.target.value ? Number(e.target.value) : '' })}
        >
          <MenuItem value="">
            <em>All</em>
          </MenuItem>
          {statusesQuery.data?.map((s) => (
            <MenuItem key={s.id as number} value={s.id as number}>
              {String(s.name)}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          select
          size="small"
          label="Source"
          sx={{ width: 180 }}
          value={sourceId}
          onChange={(e) => updateParams({ leadSourceId: e.target.value ? Number(e.target.value) : '' })}
        >
          <MenuItem value="">
            <em>All</em>
          </MenuItem>
          {sourcesQuery.data?.map((s) => (
            <MenuItem key={s.id as number} value={s.id as number}>
              {String(s.name)}
            </MenuItem>
          ))}
        </TextField>
        {!isCounselor && (
          <TextField
            select
            size="small"
            label="Counselor"
            sx={{ width: 180 }}
            value={assignedToId}
            onChange={(e) => updateParams({ assignedToId: e.target.value ? Number(e.target.value) : '' })}
          >
            <MenuItem value="">
              <em>All</em>
            </MenuItem>
            {counselors.map((c) => (
              <MenuItem key={c.id} value={c.id}>
                {c.firstName} {c.lastName}
              </MenuItem>
            ))}
          </TextField>
        )}
        {!isCounselor && (
          <TextField
            select
            size="small"
            label="Assignment status"
            sx={{ width: 170 }}
            value={assignmentStatus}
            onChange={(e) => updateParams({ assignment: e.target.value })}
          >
            <MenuItem value="">
              <em>All</em>
            </MenuItem>
            <MenuItem value="assigned">Assigned</MenuItem>
            <MenuItem value="unassigned">Unassigned</MenuItem>
          </TextField>
        )}
        {!isCounselor && (
          <TextField
            select
            size="small"
            label="Assignment type"
            sx={{ width: 190 }}
            value={assignmentType}
            onChange={(e) => updateParams({ assignmentType: e.target.value })}
          >
            <MenuItem value="">
              <em>All</em>
            </MenuItem>
            <MenuItem value="UNASSIGNED">Unassigned</MenuItem>
            <MenuItem value="COUNSELOR_CREATED">Counselor Created</MenuItem>
            <MenuItem value="CENTRAL_ASSIGNED">Central Assigned</MenuItem>
            <MenuItem value="TRANSFERRED">Transferred</MenuItem>
          </TextField>
        )}
        <TextField
          select
          size="small"
          label="Approval"
          sx={{ width: 170 }}
          value={approvalStatus}
          onChange={(e) => updateParams({ approvalStatus: e.target.value })}
        >
          <MenuItem value="">
            <em>All</em>
          </MenuItem>
          <MenuItem value="PENDING_APPROVAL">Pending Approval</MenuItem>
          <MenuItem value="APPROVED">Approved</MenuItem>
          <MenuItem value="REJECTED">Rejected</MenuItem>
        </TextField>
        <TextField
          select
          size="small"
          label="Pending"
          sx={{ width: 150 }}
          value={pendingOnly}
          onChange={(e) => updateParams({ pending: e.target.value })}
        >
          <MenuItem value="">
            <em>All</em>
          </MenuItem>
          <MenuItem value="true">Pending only</MenuItem>
          <MenuItem value="false">Not pending</MenuItem>
        </TextField>
        <SavedFiltersBar
          pageKey="leads"
          currentFilters={{ search, statusId, sourceId }}
          onApply={(f) => updateParams({ search: f.search, leadStatusId: f.statusId, leadSourceId: f.sourceId }, { resetPage: true })}
        />
      </Stack>

      <Box sx={{ height: 560, bgcolor: 'background.paper', borderRadius: 1 }}>
        <DataGrid
          rows={leadsQuery.data?.data ?? []}
          columns={columns}
          rowCount={leadsQuery.data?.meta.total ?? 0}
          loading={leadsQuery.isFetching}
          paginationMode="server"
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          pageSizeOptions={PAGE_SIZE_OPTIONS}
          sortModel={sortModel}
          onSortModelChange={setSortModel}
          slots={{ toolbar: TableToolbar }}
          checkboxSelection
          rowSelectionModel={selection}
          onRowSelectionModelChange={setSelection}
          onRowClick={(params) => {
            if (params.row.leadStatus.name === 'Draft') {
              setResumeLeadId(params.row.id);
              setWizardOpen(true);
            } else {
              // Lead Details' back button returns to this exact list.
              navigate(`/leads/${params.row.id}`, { state: leadDetailState(location) });
            }
          }}
        />
      </Box>

      <LeadRegistrationWizard
        open={wizardOpen}
        resumeLeadId={resumeLeadId}
        onClose={() => setWizardOpen(false)}
        onSaved={invalidateLeads}
      />

      <QuickLeadDialog
        open={quickLeadOpen}
        onClose={() => setQuickLeadOpen(false)}
        onSaved={(lead) => {
          invalidateLeads();
          navigate(`/leads/${lead.id}`, { state: leadDetailState(location) });
        }}
      />

      <BulkAssignDialog
        open={bulkAssignOpen}
        leadCount={selection.length}
        errorMessage={bulkAssignError}
        onClose={() => setBulkAssignOpen(false)}
        onSubmit={async (userId, reason) => {
          await bulkAssignMutation.mutateAsync({ userId, reason });
        }}
      />

      <BulkWhatsAppDialog
        open={bulkWhatsAppOpen}
        leadCount={selection.length}
        errorMessage={bulkWhatsAppError}
        resultSummary={bulkWhatsAppResult}
        onClose={() => setBulkWhatsAppOpen(false)}
        onSubmit={async (input) => {
          await bulkWhatsAppMutation.mutateAsync(input);
        }}
      />

      <BulkTransferDialog
        open={bulkTransferOpen}
        leadCount={selection.length}
        errorMessage={bulkTransferError}
        onClose={() => setBulkTransferOpen(false)}
        onSubmit={async (toBranchId, reason) => {
          await bulkTransferMutation.mutateAsync({ toBranchId, reason });
        }}
      />

      <BulkStatusDialog
        open={bulkStatusOpen}
        leadCount={selection.length}
        errorMessage={bulkStatusError}
        onClose={() => setBulkStatusOpen(false)}
        onSubmit={async (leadStatusId) => {
          await bulkStatusMutation.mutateAsync(leadStatusId);
        }}
      />

      <ImportLeadsDialog open={importOpen} onClose={() => setImportOpen(false)} onImported={invalidateLeads} />
    </Box>
  );
};

const extractErrorMessage = (err: unknown): string => {
  if (isAxiosError(err)) {
    return err.response?.data?.message ?? 'Something went wrong.';
  }
  return 'Something went wrong.';
};
